/**
 * @typedef {Object} JwtDecodeOptions
 * @property {boolean} [header] - If true, decodes the JWT header instead of payload
 */

/**
 * @typedef {Object} JwtHeader
 * @property {string} [typ]
 * @property {string} [alg]
 * @property {string} [kid]
 */

/**
 * @typedef {Object} JwtPayload
 * @property {string} [iss]
 * @property {string} [sub]
 * @property {string[] | string} [aud]
 * @property {number} [exp]
 * @property {number} [nbf]
 * @property {number} [iat]
 * @property {string} [jti]
 */

/**
 * Custom error thrown when JWT is invalid.
 */
export class InvalidTokenError extends Error {
  constructor(message) {
    super(message);
    this.name = 'InvalidTokenError';
  }
}

/**
 * Decodes a Base64 string into a UTF-8 string.
 *
 * @param {string} str - Base64 encoded string
 * @returns {string}
 */
function b64DecodeUnicode(str) {
  return decodeURIComponent(
    atob(str).replace(/(.)/g, (_, p) => {
      let code = p.charCodeAt(0).toString(16).toUpperCase();
      if (code.length < 2) {
        code = `0${code}`;
      }
      return `%${code}`;
    })
  );
}

/**
 * Decodes a Base64URL string.
 *
 * @param {string} str - Base64URL encoded string
 * @returns {string}
 */
function base64UrlDecode(str) {
  let output = str.replace(/-/g, '+').replace(/_/g, '/');

  switch (output.length % 4) {
    case 0:
      break;
    case 2:
      output += '==';
      break;
    case 3:
      output += '=';
      break;
    default:
      throw new Error('base64 string is not of the correct length');
  }

  try {
    return b64DecodeUnicode(output);
  } catch {
    return atob(output);
  }
}

/**
 * Decodes a JWT token.
 *
 * If `options.header === true`, decodes the header.
 * Otherwise decodes the payload.
 *
 * Note: This function does NOT validate the token signature.
 *
 * @template {JwtHeader | JwtPayload} T
 * @param {string} token - JWT string
 * @param {JwtDecodeOptions} [options]
 * @returns {T}
 * @throws {InvalidTokenError} If token is malformed or invalid
 */
export function jwtDecode(token, options = {}) {
  if (typeof token !== 'string') {
    throw new InvalidTokenError('Invalid token specified: must be a string');
  }

  const pos = options.header === true ? 0 : 1;
  const part = token.split('.')[pos];

  if (typeof part !== 'string') {
    throw new InvalidTokenError(
      `Invalid token specified: missing part #${pos + 1}`
    );
  }

  let decoded;

  try {
    decoded = base64UrlDecode(part);
  } catch (e) {
    throw new InvalidTokenError(
      `Invalid token specified: invalid base64 for part #${pos + 1} (${
        e.message
      })`
    );
  }

  try {
    return JSON.parse(decoded);
  } catch (e) {
    throw new InvalidTokenError(
      `Invalid token specified: invalid json for part #${pos + 1} (${
        e.message
      })`
    );
  }
}
